import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Uploadfile from "../../../../../components/uploadFiles";
import env from "../../../../../config/env";
import { useUserInfo } from "../../../../../context/user";
import { useSharedKnowledgeFiles, useDeleteSharedKnowledgeFiles } from "../../../../../queries/knowledgeBase";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  Search,
  FileText,
  Trash2,
  Download,
  Loader2,
  Link2,
} from "lucide-react";

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return "Updated today";
  if (days < 7) return `Updated ${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `Updated ${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Updated ${months}mo ago`;
  return `Updated ${Math.floor(days / 365)}y ago`;
};

const getFileType = (name) => {
  if (!name) return "FILE";
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
};

const SharedKnowledge = ({ onAddUrl, refreshSignal = 0 }) => {
  const BASE_URL = `${env.API_URL}/v1`;
  const userInfo = useUserInfo()[0];
  const userActiveLocation = useUserInfo()[4];
  const profileId = userActiveLocation?.id;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  const { data: files = [], isLoading: loading, refetch: fetchFiles } = useSharedKnowledgeFiles(profileId);
  const deleteFiles = useDeleteSharedKnowledgeFiles(profileId, userInfo?._id);

  useEffect(() => {
    if (refreshSignal) fetchFiles();
  }, [refreshSignal, fetchFiles]);

  const handleDelete = async (fileId) => {
    if (!profileId || !userInfo?._id) return;
    try {
      await deleteFiles.mutateAsync(fileId);
      setSelectedItems((prev) => prev.filter((id) => id !== fileId));
      toast.success("File deleted");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete file");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;
    try {
      await deleteFiles.mutateAsync(selectedItems);
      setSelectedItems([]);
      toast.success("File deleted");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete file");
    }
  };

  const handleDownloadFile = async (file_id) => {
    console.log(file_id, "file_id");

    setDownloadingFileId(file_id);
    try {
      const win = window.open(`${BASE_URL}/chat/assistants/files/download?file_id=${file_id}&isStreaming=true`, '_blank')
      // setTimeout(() => win.close(), 3000)
      setDownloadingFileId(null);
    } catch (error) {
      console.error("Error downloading file:", error.message);
      setDownloadingFileId(null);
    }
  };

  const toggleCheckbox = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const rows = files.filter((r) =>
    (r.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <p style={{ marginBottom: "4px", fontSize: "18px" }}>
        Knowledge added here is automatically available to all agents.{" "}
        <strong>
          Best for brand guidelines, policies, and universal instructions.
        </strong>
      </p>
      <p className="text-muted-foreground mb-5 text-sm">
        Future agents will inherit this knowledge by default.
      </p>

      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="search_box relative">
          <Search className="absolute top-3 left-3 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            className="!pl-9 !w-[740px]"
            placeholder="Search for Files"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", gap: "12px", flexShrink: 0 }}>
          <Uploadfile
            classname="filecogChatBtn"
            refreshchat={fetchFiles}
            compType="shared_knowledge"
            isSharedKnowledge
          />
          <Button
            variant="outline"
            className="ml-5"
            onClick={onAddUrl}
            aria-label="Add URL"
          >
            <Link2 className="w-4 h-4" />
            <span>Add URL</span>
          </Button>
        </div>
      </div >

      <Button
        variant="outline"
        className="mb-5"
        onClick={handleDeleteSelected}
        disabled={selectedItems.length === 0}
      >
        <Trash2 className="w-4 h-4" />
        Delete All
      </Button>

      <div>
        <div className="flex items-center text-muted-foreground font-semibold text-[13px] px-4 mb-2">
          <p style={{ padding: "12px 8px", width: "6%" }}></p>
          <p style={{ padding: "12px 8px", width: "22%" }}>File Name</p>
          <p style={{ padding: "12px 8px", width: "12%" }}>Type</p>
          <p style={{ padding: "12px 8px", width: "16%" }}>Last Updated</p>
          <p style={{ padding: "12px 8px", width: "14%" }}>Used By</p>
          <p style={{ padding: "12px 8px", width: "14%" }}>Uploaded By</p>
          <p style={{ padding: "12px 8px", width: "16%" }}></p>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", padding: "16px" }}>Loading...</p>
        ) : rows.length > 0 ? (
          <TooltipProvider>
            {rows.map((item, index) => {
              console.log(item, "item123123");

              return (
                <div
                  key={index}
                  className="flex items-center bg-card border border-border rounded-[4px] mb-[10px] px-5 text-sm"
                >
                  <p style={{ padding: "16px 8px", width: "6%" }}>
                    <Checkbox
                      className="w-[18px] h-[18px] cursor-pointer"
                      aria-label={`Select ${item.name}`}
                      checked={selectedItems.includes(item.id)}
                      onCheckedChange={() => toggleCheckbox(item.id)}
                    />
                  </p>
                  <p
                    style={{ padding: "16px 8px", width: "22%" }}
                    className="flex items-center"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center">
                          <span className="pr-2 flex">
                            <FileText className="w-4 h-4" />
                          </span>
                          {item.name.length > 15
                            ? item.name.slice(0, 15) + "..."
                            : item.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{item.name}</TooltipContent>
                    </Tooltip>
                  </p>
                  <p style={{ padding: "16px 8px", width: "12%" }}>
                    {getFileType(item.name)}
                  </p>
                  <p style={{ padding: "16px 8px", width: "16%" }}>
                    {timeAgo(item.created_at)}
                  </p>
                  <p style={{ padding: "16px 8px", width: "14%" }}>
                    <span className="bg-muted text-foreground px-3 py-1 rounded-full text-[13px]">
                      All agents
                    </span>
                  </p>

                  <p
                    style={{ padding: "16px 8px", width: "14%" }}
                    className="flex items-center"
                  >
                    {item?.user_name}
                  </p>

                  <p
                    style={{
                      padding: "16px 8px",
                      width: "16%",
                      paddingRight: "35px",
                    }}
                    className="text-right flex justify-end items-center"
                  >
                    <button
                      type="button"
                      aria-label="Download file"
                      onClick={() => handleDownloadFile(index)}
                      disabled={
                        downloadingFileId !== null && downloadingFileId !== item.id
                      }
                      className="flex items-center mr-2 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
                    >
                      {downloadingFileId === item.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5" />
                      )}
                    </button>

                    <button
                      type="button"
                      aria-label="Delete file"
                      onClick={() => handleDelete(item.id)}
                      className="bg-transparent border-none cursor-pointer text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </p>
                </div>
              );
            })}
          </TooltipProvider>
        ) : (
          <p style={{ textAlign: "center", padding: "16px" }}>No files found.</p>
        )}
      </div>
    </>
  );
};

export default SharedKnowledge;
